import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowLeft, Star, Compass, Crown, Award, Leaf, MapPin, Calendar, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function TestimonialsCarousel({ testimonials }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const showArrows = testimonials.length > 3;

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", checkScroll, { passive: true });
    window.addEventListener("resize", checkScroll);
    return () => {
      if (el) el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [checkScroll, testimonials]);

  function scroll(dir) {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector("[data-card]")?.offsetWidth || 380;
    el.scrollBy({ left: dir * (cardWidth + 32), behavior: "smooth" });
  }

  return (
    <section className="py-32 px-4 bg-zinc-900 text-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-5xl font-serif mb-6">Traveler Stories</h2>
          <div className="w-24 h-1 bg-amber-600 mx-auto" />
        </div>

        <div className="relative">
          {/* Left Arrow */}
          {showArrows && canScrollLeft && (
            <button
              onClick={() => scroll(-1)}
              className="absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-700 text-white flex items-center justify-center shadow-xl transition-all duration-200 hover:scale-110"
              data-testid="testimonials-scroll-left"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className={`flex gap-8 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 ${showArrows ? "px-2" : ""}`}
            style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
          >
            {testimonials.map((testimonial, idx) => (
              <Card
                key={testimonial.testimonial_id || idx}
                data-card
                data-testid={`testimonial-card-${idx}`}
                className={`bg-zinc-800 border-zinc-700 hover:bg-zinc-750 transition-all duration-300 hover:scale-[1.02] rounded-sm snap-start shrink-0 ${
                  showArrows ? "w-[calc(33.333%-22px)] min-w-[320px]" : "flex-1 min-w-0"
                }`}
              >
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <p className="text-zinc-300 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                  <div className="border-t border-zinc-700 pt-4">
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-zinc-400">{testimonial.location}</p>
                    <p className="text-xs text-amber-500 mt-2">{testimonial.tour}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Right Arrow */}
          {showArrows && canScrollRight && (
            <button
              onClick={() => scroll(1)}
              className="absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-700 text-white flex items-center justify-center shadow-xl transition-all duration-200 hover:scale-110"
              data-testid="testimonials-scroll-right"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [tours, setTours] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [siteContent, setSiteContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/content/tours`).then(r => r.json()),
      fetch(`${API_URL}/api/content/testimonials`).then(r => r.json()),
      fetch(`${API_URL}/api/content/site`).then(r => r.json()),
    ]).then(([t, test, site]) => {
      setTours(t);
      setTestimonials(test);
      setSiteContent(site);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const getIcon = (iconName) => {
    const icons = { compass: Compass, crown: Crown, award: Award, leaf: Leaf };
    const IconComponent = icons[iconName] || Compass;
    return <IconComponent className="w-8 h-8" />;
  };

  const filteredTours = activeCategory === 'all'
    ? tours
    : tours.filter(tour => tour.categoryType === activeCategory);

  const heroData = siteContent?.hero || {
    title: "Discover the Extraordinary in Comfort",
    subtitle: "Curated adventures for the discerning traveler",
    backgroundImage: ""
  };
  const brandValues = siteContent?.brandValues || [];
  const seo = siteContent?.seo || {};

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{seo.metaTitle || "Trailz - Premium Himalayan Adventures"}</title>
        {seo.metaDescription && <meta name="description" content={seo.metaDescription} />}
        {seo.metaKeywords && <meta name="keywords" content={seo.metaKeywords} />}
        {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
        {seo.metaTitle && <meta property="og:title" content={seo.metaTitle} />}
        {seo.metaDescription && <meta property="og:description" content={seo.metaDescription} />}
      </Helmet>
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroData.backgroundImage})`,
            transform: `translateY(${scrollY * 0.5}px)`,
            scale: 1 + scrollY * 0.0001
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-serif text-white mb-6 animate-fade-in"
              style={{ textShadow: '2px 2px 20px rgba(0,0,0,0.5)' }}>
            {heroData.title}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 font-light tracking-wide">
            {heroData.subtitle}
          </p>
          <div className="flex gap-6 justify-center flex-wrap">
            <Link to="/build-adventure">
              <Button
                size="lg"
                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-10 py-7 text-lg rounded-sm transition-all duration-300 hover:scale-105 shadow-2xl font-semibold"
              >
                Build Your Adventure <ArrowRight className="ml-2" />
              </Button>
            </Link>
            <Button
              size="lg"
              className="bg-emerald-800 hover:bg-emerald-900 text-white px-8 py-6 text-lg rounded-sm transition-all duration-300 hover:scale-105 shadow-2xl"
              onClick={() => document.getElementById('tours').scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Our Journeys
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-emerald-900 px-8 py-6 text-lg rounded-sm transition-all duration-300 hover:scale-105 backdrop-blur-sm"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Plan Your Adventure
            </Button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white rounded-full" />
          </div>
        </div>
      </section>

      {/* Brand Values Section */}
      <section className="py-32 px-4 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-serif mb-6">The Trailz Difference</h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {brandValues.map((value, index) => (
              <div
                key={index}
                className="text-center group cursor-pointer"
                style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards` }}
              >
                <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-700 to-emerald-900 text-white group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  {getIcon(value.icon)}
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-amber-500 transition-colors">
                  {value.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tours Section */}
      <section id="tours" className="py-32 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-serif mb-6 text-zinc-900">Our Curated Journeys</h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-8" />
            <p className="text-xl text-zinc-600 max-w-3xl mx-auto">
              Each journey is meticulously crafted to deliver unforgettable experiences
            </p>
          </div>

          <div className="flex justify-center gap-4 mb-16 flex-wrap">
            <Button onClick={() => setActiveCategory('all')} variant={activeCategory === 'all' ? 'default' : 'outline'}
              className={`px-8 py-6 rounded-sm transition-all duration-300 ${activeCategory === 'all' ? 'bg-emerald-800 text-white hover:bg-emerald-900' : 'border-2 border-zinc-300 text-zinc-700 hover:border-emerald-800 hover:text-emerald-800'}`}>
              All Journeys
            </Button>
            <Button onClick={() => setActiveCategory('adventurous')} variant={activeCategory === 'adventurous' ? 'default' : 'outline'}
              className={`px-8 py-6 rounded-sm transition-all duration-300 ${activeCategory === 'adventurous' ? 'bg-emerald-800 text-white hover:bg-emerald-900' : 'border-2 border-zinc-300 text-zinc-700 hover:border-emerald-800 hover:text-emerald-800'}`}>
              Untamed Trails
            </Button>
            <Button onClick={() => setActiveCategory('luxury')} variant={activeCategory === 'luxury' ? 'default' : 'outline'}
              className={`px-8 py-6 rounded-sm transition-all duration-300 ${activeCategory === 'luxury' ? 'bg-emerald-800 text-white hover:bg-emerald-900' : 'border-2 border-zinc-300 text-zinc-700 hover:border-emerald-800 hover:text-emerald-800'}`}>
              Timeless Journeys
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-700" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-12">
              {filteredTours.map((tour) => (
                <Card key={tour.tour_id || tour.slug} className="overflow-hidden group hover:shadow-2xl transition-all duration-500 border-0 rounded-sm">
                  <div className="relative h-80 overflow-hidden">
                    <img src={tour.heroImage} alt={tour.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute top-4 right-4 bg-amber-600 text-white px-4 py-2 text-sm font-semibold rounded-sm">{tour.category}</div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-3xl font-serif text-white mb-2">{tour.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-8 bg-zinc-50">
                    <p className="text-zinc-600 mb-6 leading-relaxed">{tour.shortDescription}</p>
                    <div className="flex items-center gap-6 mb-6 text-sm text-zinc-600">
                      <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-emerald-700" /><span>{tour.duration}</span></div>
                      <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-700" /><span>{tour.location}</span></div>
                      {tour.price && <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-700" /><span className="font-semibold">Starting at {tour.currency} {tour.price}</span></div>}
                    </div>
                    <Link to={`/tour/${tour.slug}`}>
                      <Button className="w-full bg-emerald-800 hover:bg-emerald-900 text-white py-6 rounded-sm transition-all duration-300 group-hover:bg-amber-600">
                        Explore Journey <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsCarousel testimonials={testimonials} />

      {/* CTA Section */}
      <section className="relative py-32 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroData.backgroundImage})` }} />
        <div className="absolute inset-0 bg-emerald-900/85" />
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <h2 className="text-5xl md:text-6xl font-serif mb-6">Your Adventure Awaits</h2>
          <p className="text-xl mb-12 text-white/90">Let us craft the perfect Himalayan journey tailored to your dreams</p>
          <Button
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 text-white px-12 py-7 text-lg rounded-sm transition-all duration-300 hover:scale-105 shadow-2xl"
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
          >
            Start Planning Today <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
